import React, { useState, useEffect, useRef } from 'react'
import CardUI from '../../../widgets/layout/CardUI'
import FormSF from '../../../widgets/form/FormSF'
import InputTextSF from '../../../widgets/form/InputTextSF'
import InputTextAreaSF from '../../../widgets/form/InputTextAreaSF'
import LoanAmountEditor from '../../../widgets/form/LoanAmountEditor'
import InputCheckBoxUI from '../../../widgets/form/InputCheckBoxUI'
import SuccessMessageUI from '../../../widgets/form/SuccessMessageUI'
import ButtonWithSpinnerUI from '../../../widgets/action/ButtonWithSpinnerUI'
import './BroadTermsUI.css'

// Promoters multi-select component (defined at module level so it doesn't unmount on parent re-renders)
const PromotersMultiSelect = ({ options = [], value = '', onChange, error }) => {
  const wrapperRef = useRef(null)
  const othersInputRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [otherInput, setOtherInput] = useState('')

  // maintain selected as an array derived from value prop (comma-separated)
  const parseValue = (v) => (Array.isArray(v) ? v : (String(v || '').trim() === '' ? [] : String(v).split(',').map(s => s.trim()).filter(Boolean)))
  const [selected, setSelected] = useState(parseValue(value))

  // Predefined options excluding 'Not applicable' and 'Others'
  const predefinedOptions = options.filter(o => o !== 'Not applicable' && o !== 'Others')

  // Helper to check if a name is custom (not in predefined options, not 'Not applicable', and not 'Others')
  const isCustomName = (name) => name !== 'Not applicable' && name !== 'Others' && !predefinedOptions.includes(name)

  // Derived: are there custom names or is 'Others' selected?
  const isOthersSelected = selected.includes('Others') || selected.some(isCustomName)

  useEffect(() => {
    setSelected(parseValue(value))
  }, [value])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleOption = (opt) => {
    // If user selects 'Not applicable', notify parent with empty list
    if (opt === 'Not applicable') {
      setSelected(['Not applicable'])
      setOtherInput('')
      if (onChange) onChange({ target: { value: 'Not applicable' } })
      setOpen(false)
      return
    }

    if (opt === 'Others') {
      if (!isOthersSelected) {
        // Selecting Others: remove 'Not applicable', add 'Others' to selected
        const filtered = selected.filter(s => s !== 'Not applicable')
        const next = filtered.includes('Others') ? filtered : [...filtered, 'Others']
        setSelected(next)
        if (onChange) onChange({ target: { value: next.join(', ') } })
        // Keep dropdown open and focus the input field inside Others
        setTimeout(() => othersInputRef.current && othersInputRef.current.focus(), 50)
      } else {
        // Unchecking Others: remove 'Others' and any custom names
        const remaining = selected.filter(s => predefinedOptions.includes(s))
        setSelected(remaining)
        setOtherInput('')
        if (onChange) onChange({ target: { value: remaining.join(', ') } })
      }
      return
    }

    // Predefined options toggle
    const exists = selected.includes(opt)
    const filtered = selected.filter(s => s !== 'Not applicable')
    const next = exists ? filtered.filter(s => s !== opt) : [...filtered, opt]
    setSelected(next)
    if (onChange) onChange({ target: { value: next.join(', ') } })
  }

  const removeTag = (opt) => {
    const next = selected.filter(s => s !== opt)
    setSelected(next)
    if (opt === 'Not applicable') {
      if (onChange) onChange({ target: { value: '' } })
    } else {
      if (onChange) onChange({ target: { value: next.join(', ') } })
    }
  }

  const handleOtherInputChange = (e) => {
    // Silently allow letters, spaces, and commas only
    const clean = e.target.value.replace(/[^a-zA-Z\s,]/g, '')
    setOtherInput(clean)
  }

  const addOtherName = () => {
    const trimmed = otherInput && String(otherInput).trim()
    if (!trimmed) return

    // Split comma-separated names if entered
    const namesToAdd = trimmed.split(',').map(s => s.trim().replace(/\s+/g, ' ')).filter(Boolean)
    const validNames = namesToAdd.filter(n => /^[a-zA-Z\s]+$/.test(n))

    if (validNames.length === 0) {
      return
    }

    // Filter out duplicates
    const uniqueNames = validNames.filter(n => !selected.includes(n) && n.toLowerCase() !== 'others')
    if (uniqueNames.length === 0) {
      setOtherInput('')
      return
    }

    // Remove 'Not applicable' and remove 'Others' placeholder now that actual name(s) exist
    const filtered = selected.filter(s => s !== 'Not applicable' && s !== 'Others')
    const next = [...filtered, ...uniqueNames]
    setSelected(next)
    if (onChange) onChange({ target: { value: next.join(', ') } })
    setOtherInput('')
    setTimeout(() => othersInputRef.current && othersInputRef.current.focus(), 50)
  }

  return (
    <div className='su_broadterms_promoters' ref={wrapperRef}>
      <div className={`su_broadterms_multi_input ${open ? 'open' : ''}`} onClick={() => setOpen(prev => !prev)} role='button' tabIndex={0}>
        {selected.length === 0 && <span className='su_broadterms_placeholder'>Select guarantors...</span>}
        {selected.map(s => (
          <span key={s} className='su_broadterms_tag'>
            <span className='su_broadterms_tag_text'>{s}</span>
            <button type='button' className='su_broadterms_tag_x' onClick={(e) => { e.stopPropagation(); removeTag(s) }}>&times;</button>
          </span>
        ))}
      </div>
      {error && <div className='g_form_error'>{error}</div>}
      {open && (
        <div className='su_broadterms_dropdown'>
          {/* Ensure 'Not applicable' is available as an option first */}
          <div className={`su_broadterms_option ${selected.includes('Not applicable') ? 'selected' : ''}`} onClick={() => toggleOption('Not applicable')}>
            <input type='checkbox' readOnly checked={selected.includes('Not applicable')} />
            <span className='su_broadterms_option_text'>Not applicable</span>
          </div>
          {/* Predefined options */}
          {predefinedOptions.map(opt => (
            <div key={opt} className={`su_broadterms_option ${selected.includes(opt) ? 'selected' : ''}`} onClick={() => toggleOption(opt)}>
              <input type='checkbox' readOnly checked={selected.includes(opt)} />
              <span className='su_broadterms_option_text'>{opt}</span>
            </div>
          ))}
          {/* Others option with inline input */}
          <div className='su_broadterms_others_container'>
            <div className={`su_broadterms_option ${isOthersSelected ? 'selected' : ''}`} onClick={() => toggleOption('Others')}>
              <input type='checkbox' readOnly checked={isOthersSelected} />
              <span className='su_broadterms_option_text'>Others</span>
            </div>

            {/* Inline input section inside Others */}
            {isOthersSelected && (
              <div className='su_broadterms_others_input_section' onClick={(e) => e.stopPropagation()}>
                <div className='su_broadterms_others_input_row'>
                  <input
                    ref={othersInputRef}
                    className='su_broadterms_other_name_input'
                    type='text'
                    placeholder='Enter guarantor name'
                    value={otherInput}
                    onChange={handleOtherInputChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addOtherName()
                      }
                    }}
                  />
                  <button type='button' className='su_add_btn su_add_plus_btn' onClick={addOtherName} title='Add'>+</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const BroadTermsUI = ({
  context,
  loan,
  // status section
  onPreview,
  onSend,
  sentDate,
  previewSubmitted,
  sendSubmitted,
  // fields
  borrower, onBorrowerChange, borrowerError, setBorrowerError,
  promoters, onPromotersChange, promotersError, setPromotersError,
  purpose, onPurposeChange, purposeError, setPurposeError,
  loanAmount, onLoanAmountChange, loanAmountError, setLoanAmountError,
  loanTenor, onLoanTenorChange, loanTenorError, setLoanTenorError,
  amortizedMonths, onAmortizedMonthsChange, amortizedMonthsError, setAmortizedMonthsError,
  revolvingMonths, onRevolvingMonthsChange, revolvingMonthsError, setRevolvingMonthsError,
  interestRate, onInterestRateChange, interestRateError, setInterestRateError,
  processingFees, onProcessingFeesChange, processingFeesError, setProcessingFeesError,
  // dsra
  dsraMonths, onDsraMonthsChange, dsraMonthsError, setDsraMonthsError,
  dsraPercent, onDsraPercentChange, dsraPercentError, setDsraPercentError,
  // pledge vs ndu
  sharesPledgedPercent, onSharesPledgedPercentChange, sharesPledgedPercentError, setSharesPledgedPercentError,
  nduPercent, onNduPercentChange, nduPercentError, setNduPercentError,
  // co-borrower pledge vs ndu
  coSharesPledgedPercent, onCoSharesPledgedPercentChange, coSharesPledgedPercentError, setCoSharesPledgedPercentError,
  coNduPercent, onCoNduPercentChange, coNduPercentError, setCoNduPercentError,
  // upfront security breakdown
  upfrontSecurityFirstExclusive, onUpfrontSecurityFirstExclusiveChange, upfrontSecurityFirstExclusiveError, setUpfrontSecurityFirstExclusiveError,
  upfrontSecurityFirstPariPassu, onUpfrontSecurityFirstPariPassuChange, upfrontSecurityFirstPariPassuError, setUpfrontSecurityFirstPariPassuError,
  upfrontSecuritySubservient, onUpfrontSecuritySubservientChange, upfrontSecuritySubservientError, setUpfrontSecuritySubservientError,
  addSecurity, onAddSecurityChange, addSecurityError, setAddSecurityError,
  // optional refresh handler
  onRefreshClick,
  // co-borrower
  coBorrower, onCoBorrowerChange,
  // form
  onSubmit, submitted, successMsg, errorList, errorCode, setErrorList,
  // escrow routes
  selectedEscrowRoute, onEscrowRouteChange,
  // recipient emails
  recipientEmails, onAddRecipientEmail, onRemoveRecipientEmail,
}) => {
  const status = loan && loan.broadTerms ? loan.broadTerms : null
  const statusValue = status && (status.status || status.broadTermsStatus)
  // prefer broadTermsCreatedDate if provided by API
  const createdDate = status && (status.broadTermsCreatedDate || status.createdAtF1 || status.createdDateF1 || status.createdAt || status.createdDate)
  // prefer broadTermsSentBy / broadTermsSentDate if provided by API, else fallback to existing fields
  const sentBy = status && (status.broadTermsSentBy || ((status.sentBy && (status.sentBy.name || status.sentBy.userName)) || status.sentByName))
  const sentDateDisplay = status && (status.broadTermsSentDate || sentDate || status.sentDate)

  // determine button enabled/disabled states based on broadTermsStatus
  const broadTermsStatus = status && (status.broadTermsStatus || status.status)
  // When 'broad_terms_not_generated' -> disable Preview and Send
  // When 'broad_terms_generated' -> enable Preview and Send
  // When 'broad_terms_sent' -> enable Preview, disable Send and show 'Already Sent'
  const isNotGenerated = broadTermsStatus === 'broad_terms_not_generated'
  const isSent = broadTermsStatus === 'broad_terms_sent'

  // determine whether Generate Broad Terms button should be enabled
  // Escrow routes single-select state (checkbox-like UI but only one may be selected)
  const initialEscrow = (selectedEscrowRoute && selectedEscrowRoute) || (loan && loan.broadTerms && loan.broadTerms.selectedEscrow) || ''
  const [selectedEscrow, setSelectedEscrow] = useState(initialEscrow)
  useEffect(() => {
    setSelectedEscrow(selectedEscrowRoute || '')
  }, [selectedEscrowRoute])

  const toggleEscrow = (opt) => {
    const next = selectedEscrow === opt ? '' : opt
    setSelectedEscrow(next)
    if (onEscrowRouteChange) onEscrowRouteChange(next)
  }

  // local input for recipient email before adding to parent list
  const [recipientInput, setRecipientInput] = useState('')
  const addRecipient = () => {
    const v = recipientInput && String(recipientInput).trim()
    if (!v) return
    if (onAddRecipientEmail) onAddRecipientEmail(v)
    setRecipientInput('')
  }

  const requiredFilled = loanAmount && String(loanAmount).trim() !== '' && interestRate && String(interestRate).trim() !== '' && purpose && String(purpose).trim() !== '' && processingFees && String(processingFees).trim() !== '' && loanTenor && String(loanTenor).trim() !== ''
  const dsraProvided = (dsraMonths && String(dsraMonths).trim() !== '') || (dsraPercent && String(dsraPercent).trim() !== '')
  const primaryPledgeOrNdu = (sharesPledgedPercent && String(sharesPledgedPercent).trim() !== '') || (nduPercent && String(nduPercent).trim() !== '')
  const coPledgeOrNduProvided = (coSharesPledgedPercent && String(coSharesPledgedPercent).trim() !== '') || (coNduPercent && String(coNduPercent).trim() !== '')
  const coProvided = coBorrower && String(coBorrower).trim() !== ''
  const pledgeOrNduProvided = coProvided ? coPledgeOrNduProvided : primaryPledgeOrNdu
  // escrow selection must be non-empty
  const escrowProvided = selectedEscrow && String(selectedEscrow).trim() !== ''
  // require at least one upfront security breakdown field to be provided
  const upfrontSecurityProvided = (
    (upfrontSecurityFirstExclusive && String(upfrontSecurityFirstExclusive).trim() !== '') ||
    (upfrontSecurityFirstPariPassu && String(upfrontSecurityFirstPariPassu).trim() !== '') ||
    (upfrontSecuritySubservient && String(upfrontSecuritySubservient).trim() !== '')
  )
  const [showLoanTenure, setShowLoanTenure] = useState(false);
  const [loanTenureMonths, setLoanTenureMonths] = useState('');
  const [loanTenureMonthsError, setLoanTenureMonthsError] = useState('');
  const [showRevolving, setShowRevolving] = useState(false);
  const onShowLoanTenure = (e) => {
    const checked = e.target.checked;
    setShowLoanTenure(checked);
    if (checked) {
      setShowRevolving(false); // turn off revolving
    }
  };
  const onShowRevolving = (e) => {
    const checked = e.target.checked;
    setShowRevolving(checked);
    if (checked) {
      setShowLoanTenure(false); // turn off amortized
    }
  };

  const onLoanTenureMonthsChange = (e) => setLoanTenureMonths(e.target.value);
  // promoters (personal guarantors) must be provided; allow empty array to mean 'Not applicable'
  const promotersProvided = (promoters !== null && promoters !== undefined) && (Array.isArray(promoters) || String(promoters).trim() !== '')

  const generateEnabled = Boolean(requiredFilled && dsraProvided && pledgeOrNduProvided && escrowProvided && upfrontSecurityProvided && promotersProvided)

  // use CSS classes instead of inline styles for the generate button
  const generateButtonClass = generateEnabled ? 'g_btn_primary' : undefined

  // confirm before sending broad terms to avoid accidental sends
  const confirmAndSend = () => {
    if (!onSend) return
    const msg = 'Are you sure you want to send the Broad Terms to the borrower? This will send and notify the borrower.'
    if (window.confirm(msg)) {
      onSend()
    }
  }

  const renderPromotersContent = () => {
    const promoterOptions = (loan && loan.broadTerms && Array.isArray(loan.broadTerms.promoters)) ? loan.broadTerms.promoters : []
    if (onPromotersChange) {
      return <PromotersMultiSelect options={promoterOptions} value={promoters} onChange={onPromotersChange} error={promotersError} />
    }
    return <div>{(promoters || (promoterOptions.length > 0 ? promoterOptions.join(', ') : '—'))}</div>
  }

  return (
    <CardUI context={context} className='su_ts'>
      <div className='su_broadterms_header'>
        <div className='su_broadterms_status'>
          <div className='su_status_heading'>Broad Terms overview</div>
          <div className='su_status_item'>
            <span className='su_status_label'>Status</span>
            <span className='su_status_value'>{statusValue || '—'}</span>
          </div>
          <div className='su_status_item'>
            <span className='su_status_label'>Last updated</span>
            <span className='su_status_value'>{createdDate || '—'}</span>
          </div>
          <div className='su_status_item'>
            <span className='su_status_label'>Sent by</span>
            <span className='su_status_value'>{sentBy || '—'}</span>
          </div>
          <div className='su_status_item'>
            <span className='su_status_label'>Sent date</span>
            <span className='su_status_value'>{sentDateDisplay || '—'}</span>
          </div>
          <div className='su_status_actions_row'>
            <div className='su_status_button_group'>
              <ButtonWithSpinnerUI
                label='Preview Broad Terms'
                onClick={onPreview}
                alignLeft={true}
                busy={previewSubmitted}
                disabled={previewSubmitted || isNotGenerated}
              />
              <ButtonWithSpinnerUI
                label='Send Broad Terms'
                onClick={confirmAndSend}
                alignLeft={true}
                busy={sendSubmitted}
                disabled={sendSubmitted || isNotGenerated}
              />
            </div>
            <div className='su_recipients su_recipients_inline'>
              <div className='su_recipients_header'>Recipient emails</div>
              <div className='su_recipients_input_row'>
                <input
                  className='su_broadterms_recipient_input'
                  type='text'
                  placeholder='Add recipient email'
                  value={recipientInput}
                  onChange={(e) => setRecipientInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addRecipient()
                    }
                  }}
                />
                <button type='button' className='su_add_btn' onClick={addRecipient}>Add</button>
              </div>
            </div>
          </div>
          <div className='su_broadterms_recipient_tags su_broadterms_recipient_tags_inline'>
            {recipientEmails && recipientEmails.length > 0 ? (
              recipientEmails.map((r) => (
                <span key={r} className='su_broadterms_tag su_broadterms_recipient_tag'>
                  <span className='su_broadterms_tag_text'>{r}</span>
                  <button type='button' className='su_broadterms_tag_x' onClick={() => onRemoveRecipientEmail && onRemoveRecipientEmail(r)}>&times;</button>
                </span>
              ))
            ) : (
              <span className='su_status_label'>No recipients added</span>
            )}
          </div>
        </div>
      </div>

      <SuccessMessageUI successMsg={successMsg} />

      <FormSF
        context={context}
        buttonLabel='Generate Broad Terms'
        onSubmit={onSubmit}
        busy={submitted}
        errorList={errorList}
        errorCode={errorCode}
        setErrorList={setErrorList}
        disabled={!generateEnabled}
        buttonClassName={generateButtonClass}
      >
        <section className='su_form_section'>
          <div className='su_section_header'>Borrower details</div>
          <div className='su_field_grid su_field_grid_two su_field_grid_two_fixed'>
            <div className='su_field_block su_field_block_compact'>
              <div className='su_field_label'>Borrower</div>
              <InputTextSF
                type='text'
                name='borrower'
                value={borrower}
                disabled={true}
              />
            </div>
            <div className='su_field_block su_field_block_compact'>
              <div className='su_field_label'>Co-borrower (if applicable)</div>
              <InputTextSF
                type='text'
                name='co_borrower'
                value={coBorrower}
                onChange={onCoBorrowerChange}
              />
            </div>
            <div className='su_field_block su_field_full'>
              <div className='su_field_label'>Select Personal Guarantor(s)<span className='su_required_star'>*</span></div>
              {renderPromotersContent()}
            </div>
          </div>
          {coBorrower && String(coBorrower).trim() !== '' && (
            <>
              <div className='su_field_block su_field_full' style={{ marginTop: 24 }}>
                <div className='su_field_label'>Co-borrower Share pledge / NDU<span className='su_required_star' style={{ marginTop: 16 }}>*</span></div>
              </div>
              <div className='su_field_grid su_field_grid_two' style={{ marginTop: 8 }}>
                <div className='su_field_block su_field_block_compact'>
                  <div className='su_field_label'>Percentage of shares pledged (%)</div>
                  <InputTextSF
                    type='text'
                    name='co_shares_pledged_percent'
                    maxLength={6}
                    value={coSharesPledgedPercent}
                    onChange={onCoSharesPledgedPercentChange}
                    error={coSharesPledgedPercentError}
                    setError={setCoSharesPledgedPercentError}
                    help='Either pledged % or NDU %'
                  />
                </div>
                <div className='su_field_block su_field_block_compact'>
                  <div className='su_field_label'>Percentage of NDU (%)</div>
                  <InputTextSF
                    type='text'
                    name='co_ndu_percent'
                    maxLength={6}
                    value={coNduPercent}
                    onChange={onCoNduPercentChange}
                    error={coNduPercentError}
                    setError={setCoNduPercentError}
                    help='Either pledged % or NDU %'
                  />
                </div>
              </div>
            </>
          )}
        </section>

        <section className='su_form_section'>
          <div className='su_section_header'>Loan terms</div>
          <div className='su_field_grid su_field_grid_two'>
            <div className='su_field_block su_field_full'>
              <LoanAmountEditor
                label={<><span>Loan Amount (₹)</span><span className='su_required_star'>*</span></>}
                value={loanAmount ?
                  JSON.parse(loanAmount)
                  : ''
                }
                onChange={onLoanAmountChange}
                error={loanAmountError}
                setError={setLoanAmountError}
              />
            </div>
            <div className='su_field_block'>
              <InputTextSF
                type='text'
                name='interest_rate'
                required={true}
                label={<><span>Applicable Interest Rate</span><span className='su_required_star'>*</span></>}
                value={interestRate}
                onChange={onInterestRateChange}
                error={interestRateError}
                setError={setInterestRateError}
              />
            </div>
            <div className='su_field_block'>
              <InputTextSF
                type='percentage'
                name='processing_fees'
                required={true}
                rows={3}
                label={<><span>Processing fees</span><span className='su_required_star'>*</span></>}
                value={processingFees}
                onChange={onProcessingFeesChange}
                error={processingFeesError}
                setError={setProcessingFeesError}
              />
            </div>
            <div className='su_field_block su_loan_tenor_row'>
              {/* Loan Tenor Field */}
              <InputTextSF
                type='text'
                name='loan_tenor'
                required={true}
                label={
                  <>
                    <span>Loan Tenor (months)</span>
                    <span className='su_required_star'>*</span>
                  </>
                }
                value={loanTenor}
                onChange={onLoanTenorChange}
                error={loanTenorError}
                setError={setLoanTenorError}
              />
              <div className="su_checkbox_group">
                <InputCheckBoxUI
                  label='Loan tenure months shorter than amortized months'
                  onChange={onShowLoanTenure}
                  checked={showLoanTenure}
                  name='showLoanTenure'
                  className='su_inline_checkbox'
                />

                {showLoanTenure && (
                  <div className="su_checkbox_child">
                    <InputTextSF
                      type='text'
                      name='shorter_loan_tenure'
                      maxLength={3}
                      label={<span>Amortized Months</span>}
                      value={amortizedMonths || ''}
                      onChange={onAmortizedMonthsChange}
                      error={amortizedMonthsError}
                      setError={setAmortizedMonthsError}
                    />
                  </div>
                )}
              </div>

              <div className="su_checkbox_group">
                <InputCheckBoxUI
                  label='Revolving loan'
                  onChange={onShowRevolving}
                  checked={showRevolving}
                  name='showRevolving'
                  className='su_inline_checkbox'
                />

                {showRevolving && (
                  <div className="su_checkbox_child">
                    <InputTextSF
                      type='text'
                      name='revolving_months'
                      maxLength={3}
                      label={<span>Revolving Months</span>}
                      value={revolvingMonths}
                      onChange={onRevolvingMonthsChange}
                      error={revolvingMonthsError}
                      setError={setRevolvingMonthsError}
                    />
                  </div>
                )}
              </div>
            </div>



            <div className='su_field_block su_field_full'>
              <InputTextAreaSF
                name='purpose'
                required={true}
                rows={3}
                label={<><span>Purpose of the Loan</span><span className='su_required_star'>*</span></>}
                value={purpose}
                onChange={onPurposeChange}
                error={purposeError}
                setError={setPurposeError}
              />
            </div>
          </div>
        </section>

        <section className='su_form_section'>
          <div className='su_section_header'>Risk coverage</div>
          <div className='su_field_grid su_field_grid_two'>
            <div className='su_field_block'>
              <div className='su_field_label'>DSRA<span className='su_required_star'>*</span></div>
              <div className='su_inline_fields'>
                <InputTextSF
                  type='text'
                  name='dsra_months'
                  maxLength={3}
                  label='DSRA months'
                  value={dsraMonths}
                  onChange={onDsraMonthsChange}
                  error={dsraMonthsError}
                  setError={setDsraMonthsError}
                  help='Provide either DSRA months or DSRA %'
                />
                <InputTextSF
                  type='percentage'
                  name='dsra_percent'
                  maxLength={6}
                  label='DSRA %'
                  value={dsraPercent}
                  onChange={onDsraPercentChange}
                  error={dsraPercentError}
                  setError={setDsraPercentError}
                  help='Provide either DSRA months or DSRA %'
                />
              </div>
            </div>
            <div className='su_field_block'>
              <div className='su_field_label'>Share pledge / NDU<span className='su_required_star'>*</span></div>
              <div className='su_inline_fields'>
                <InputTextSF
                  type='percentage'
                  name='shares_pledged_percent'
                  maxLength={6}
                  label='Percentage of shares pledged (%)'
                  value={sharesPledgedPercent}
                  onChange={onSharesPledgedPercentChange}
                  error={sharesPledgedPercentError}
                  setError={setSharesPledgedPercentError}
                  help='Either pledged % or NDU %'
                />
                <InputTextSF
                  type='percentage'
                  name='ndu_percent'
                  maxLength={6}
                  label='Percentage of NDU (%)'
                  value={nduPercent}
                  onChange={onNduPercentChange}
                  error={nduPercentError}
                  setError={setNduPercentError}
                  help='Either pledged % or NDU %'
                />
              </div>
            </div>
          </div>
        </section>

        <section className='su_form_section'>
          <div className='su_section_header'>Upfront security<span className='su_required_star'>*</span></div>
          <div className='su_field_grid su_field_grid_three'>
            <div className='su_field_block'>
              <InputTextSF
                type='text'
                name='upfront_security_first_exclusive'
                label='First and exclusive'
                value={upfrontSecurityFirstExclusive}
                onChange={onUpfrontSecurityFirstExclusiveChange}
                error={upfrontSecurityFirstExclusiveError}
                setError={setUpfrontSecurityFirstExclusiveError}
              />
            </div>
            <div className='su_field_block'>
              <InputTextSF
                type='text'
                name='upfront_security_first_paripassu'
                label='First Pari-Passu'
                value={upfrontSecurityFirstPariPassu}
                onChange={onUpfrontSecurityFirstPariPassuChange}
                error={upfrontSecurityFirstPariPassuError}
                setError={setUpfrontSecurityFirstPariPassuError}
              />
            </div>
            <div className='su_field_block'>
              <InputTextSF
                type='text'
                name='upfront_security_subservient'
                label='Subservient'
                value={upfrontSecuritySubservient}
                onChange={onUpfrontSecuritySubservientChange}
                error={upfrontSecuritySubservientError}
                setError={setUpfrontSecuritySubservientError}
              />
            </div>
            <div className='su_field_block su_field_full'>
              <InputTextAreaSF
                name='purpose'
                required={true}
                rows={3}
                label={<><span>Additonal Security</span><span className='su_required_star'>*</span></>}
                value={addSecurity}
                onChange={onAddSecurityChange}
                error={addSecurityError}
                setError={setAddSecurityError}
              />
            </div>
          </div>
        </section>

        <section className='su_form_section'>
          <div className='su_section_header'>Escrow routes<span className='su_required_star'>*</span></div>
          {loan && loan.broadTerms && Array.isArray(loan.broadTerms.escrowRoutes) && loan.broadTerms.escrowRoutes.length > 0 ? (
            <div className='su_broadterms_escrow_list'>
              {loan.broadTerms.escrowRoutes.map((opt, idx) => (
                <label key={idx} className={`su_broadterms_escrow_option ${selectedEscrow === opt ? 'selected' : ''}`}>
                  <input type='checkbox' checked={selectedEscrow === opt} onChange={() => toggleEscrow(opt)} />
                  <span className='su_broadterms_escrow_text'>{opt}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className='su_text_value'>—</div>
          )}
        </section>
      </FormSF>
    </CardUI>
  )
}

export default BroadTermsUI


